import { Injectable } from '@angular/core';
import { getIDType } from '@datorama/akita';
import { UntilDestroy } from '@ngneat/until-destroy';
import { DatasourceService, DatasourceTypeQuery } from '@y42/datasource';
import {
  METADATA_JSON_SCHEMA_URL,
  UI_MODELING_MODEL_JSON_SCHEMA_URL,
  UI_MODELING_MODEL_TABLE_JSON_SCHEMA_URL,
  Y42MetadataGitModel,
} from '@y42/feature/json-schema';
import { Y42RouteDetailTabs, Y42RouterService, Y42Routes } from '@y42/feature/routing';
import { AddEntityDialogResponse } from '@y42/feature/shared/add-entity';
import { GitQuery } from '@y42/git';
import { TriggerService } from '@y42/impex';
import { DatasourceTypeEnum, UiModelingModelSettings, Y42FeatureType } from '@y42/json-schema';
import { ToastService } from '@y42/toast';
import { getSafeString } from '@y42/util/safe-string';
import uniq from 'lodash/uniq';
import {
  UiModelingGitService,
  UiModelingQuery,
  UiModelingState,
  UiModelingStore,
  UiModelingStoreModel,
} from '../../state/datasource';
import {
  UiModelingTableGitFactoryService,
  UiModelingTableQuery,
  UiModelingTableStore,
} from '../../state/datasource-table';
import { UiModelingTableJobStore } from '../../state/datasource-table-job';
import { createDefaultGojsModel, DataModelGoJSModel, DataModelGoJSNode } from '../../state/models';
import { UiModelingTableService } from '../datasource-table';
import { UiModelingTableJobService } from '../datasource-table-job/ui-modeling-table-job.service';
import { UiModelingAclService } from './ui-modeling-acl.service';
import { UiModelingUserDataService } from './ui-modeling-user-data.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class UiModelingService extends DatasourceService<UiModelingState> {
  constructor(
    protected readonly userData: UiModelingUserDataService,
    protected readonly store: UiModelingStore,
    protected readonly query: UiModelingQuery,
    protected readonly git: UiModelingGitService,
    protected readonly tableStore: UiModelingTableStore,
    protected readonly tableGitFactory: UiModelingTableGitFactoryService,
    protected readonly tableJobStore: UiModelingTableJobStore,
    protected readonly routerService: Y42RouterService,
    protected readonly toast: ToastService,
    protected readonly gitQuery: GitQuery,
    protected readonly acl: UiModelingAclService,
    protected readonly tableQuery: UiModelingTableQuery,
    protected readonly tableService: UiModelingTableService,
    private readonly datasourceTypeQuery: DatasourceTypeQuery,
    triggerService: TriggerService,
    tableJobService: UiModelingTableJobService,
  ) {
    super(
      {
        featureType: Y42FeatureType.UiModel,
        userData,
        store,
        query,
        acl,
        git,
        toast,
        tableStore,
        tableQuery,
        tableJobStore,
        tableJobService,
        gitQuery,
      },
      triggerService,
    );
  }

  async create(data: AddEntityDialogResponse) {
    const { title, description } = data;
    const $name = getSafeString(title);
    const typeModel = this.datasourceTypeQuery.findModelByType(DatasourceTypeEnum.UiModel)!;

    const model = this.store.create({
      ...new Y42MetadataGitModel(),
      $schema: METADATA_JSON_SCHEMA_URL,
      $name,
      name: $name,
      description,
      settings: {
        $schema: UI_MODELING_MODEL_JSON_SCHEMA_URL,
      },
    });
    await this.store.pending;

    await this.writeUiModel(model, createDefaultGojsModel());

    await this.git.commitChanges(`models: new ${typeModel.displayName} model "${$name}" created`, model.$path);

    await this.routerService.navigateToDetail(Y42Routes.UiModeling, $name, Y42RouteDetailTabs.Overview);
  }

  public async updateSettingsBlend(config: { model: DataModelGoJSModel; modelFilePath: string }) {
    const relatedModel = this.query.getEntity(config.modelFilePath);
    if (!relatedModel) {
      return;
    }

    const outputTables = (config.model.nodeDataArray as DataModelGoJSNode[])
      .filter((node) => node.type === 'OUTPUT')
      .map((node) => ({
        key: node.key,
        name: node.transformationStep.title || node.transformationStep.transformObject.name,
      }));

    await this.writeUiModel(relatedModel, config.model);

    const existingTables = this.tableQuery.getAll().filter((t) => t.$parent === relatedModel.$path);

    const allPossibleTableNames = uniq([
      ...existingTables.map((entity) => entity.$name),
      ...outputTables.map((outputTable) => outputTable.name),
    ]);

    for (const tableName of allPossibleTableNames) {
      const tableInOutputTables = outputTables.find((outputTable) => outputTable.name === tableName);
      const tableInExistingTables = existingTables.find((existingTable) => existingTable.$name === tableName);

      if (tableInOutputTables && tableInExistingTables) {
        // update
        const fullyEnrichedTableModel = this.tableStore.getValue().entities?.[tableInExistingTables.$path];
        await this.tableService.update(
          {
            ...fullyEnrichedTableModel,
            settings: {
              ...fullyEnrichedTableModel!.settings!,
              output_key: tableInOutputTables.key,
            },
          },
          tableInExistingTables.$path,
        );
      } else if (!tableInOutputTables && tableInExistingTables) {
        // delete
        await this.tableService.delete(tableInExistingTables.$path);
      } else if (tableInOutputTables && !tableInExistingTables) {
        // create
        await this.createUIDatasourceTable(relatedModel, tableInOutputTables.name, tableInOutputTables.key);
      }
    }
  }

  async delete($path: string = this.query.getActiveId() as getIDType<UiModelingState>) {
    const entity = this.query.getEntity($path);
    this.store.delete($path);
    await this.store.pending;
    await this.git.commitChanges(`models: model '${entity?.$name ?? $path}' deleted`, [$path]);
  }

  private async writeUiModel(model: UiModelingStoreModel, blend: UiModelingModelSettings) {
    const path = this.getUiModelFilePath(model);
    await this.git.writeUiModelFile(path, blend);
  }

  private getUiModelFilePath(model: UiModelingStoreModel) {
    return this.git.getPath(model.$name, 'ui-model.json');
  }

  private async createUIDatasourceTable(datasource: UiModelingStoreModel, name: string, output_key: string) {
    return await this.tableService.createFor(datasource, name, {
      output_key,
      $schema: UI_MODELING_MODEL_TABLE_JSON_SCHEMA_URL,
    });
  }
}
